import { TableBody, TableCell, TableRow } from "@windmill/react-ui";
import MainDrawer from "components/drawer/MainDrawer";
import DeleteModal from "components/modal/DeleteModal";
import ViewModal from "components/modal/ViewFranchiseModal";
import EditDeleteButton from "components/table/EditDeleteButton";
import useToggleDrawer from "hooks/useToggleDrawer";
import React, { useState } from "react";
import { FiEye } from "react-icons/fi";
import RemarkModal from "components/modal/RemarkModal";
import { Modal, ModalBody } from "@windmill/react-ui";
import FranchiseDrawer from "components/drawer/FranchiseDrawer";
import FranchisePageServices from "services/FranchiseService";
import FranchiseStatus from "components/table/FranchiseStatus";
import { Verified } from "lucide-react";
import { notifyError, notifySuccess } from "utils/toast";

const FranchiseTable = ({
  franchise,
  perpage,
  currentPage,
  hideEdit,
  hideDelete,
  setIsUpdate,
  tab,
}) => {
  const {
    title,
    serviceId,
    serviceBlock,
    handleModalOpen,
    handleView,
    handleUpdate,
    handleBlock,
  } = useToggleDrawer();
  const [isOpen, setIsOpen] = useState(false);
  const [openRemarks, setOpenRemarks] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [FranchiseModelData, setFranchiseModelData] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [viewReason, setViewReason] = useState("");
  const [changesModalOpen, setChangesModalOpen] = useState(false);
  const [changesModalData, setChangesModalData] = useState([]);
  const handleSuccessModalOpen = () => {
    setIsOpen(true);
  };

  const handleSuccessModalClose = () => {
    setIsOpen(false);
  };

  const FranchisegetByIdData = async (id) => {
    try {
      const res = await FranchisePageServices.getFranchisePageById(id);
      setFranchiseModelData([res?.data]); // if API returns {status,data}
    } catch (err) {
      console.log(err);
    }
  };

  const adminInfo = localStorage.getItem("adminInfo")
    ? JSON.parse(localStorage.getItem("adminInfo"))
    : null;
  const handleConfirm = async () => {
    try {
      if (actionType === "approve") {
        const res = await FranchisePageServices.updateFranchiseApprove(
          selectedId,
          {
            verified_by: adminInfo?.id,
          },
        );
        notifySuccess(res?.message);
      } else {
        if (!reason.trim()) {
          notifyError("Please enter rejection reason.");
          return;
        }

        const res = await FranchisePageServices.updateFranchiseReject(
          selectedId,
          {
            rejection_reason: reason.trim(),
            verified_by: adminInfo?.id,
          },
        );
        notifySuccess(res?.message);
      }

      setConfirmOpen(false);
      setReason("");
      setIsUpdate(true);
    } catch (err) {
      notifyError(err?.response?.data?.message);
    }
  };

  const isPdfFile = (filePath) => {
    return (
      typeof filePath === "string" && filePath.toLowerCase().endsWith(".pdf")
    );
  };

  const isImageFile = (filePath) => {
    return typeof filePath === "string" && /\.(jpg|jpeg|png)$/i.test(filePath);
  };

  const renderChangeValue = (value, change, type) => {
    if (!value || value === "—") {
      return "—";
    }

    // City
    if (change?.label === "City") {
      return type === "old"
        ? change.oldCityName || value
        : change.newCityName || value;
    }

    // State
    if (change?.label === "State") {
      return type === "old"
        ? change.oldStateName || value
        : change.newStateName || value;
    }

    // Image
    if (isImageFile(value)) {
      return (
        <img
          src={`${process.env.REACT_APP_API_IMAGE_URL}${value}`}
          alt="Document"
          className="w-20 h-20 object-cover rounded border cursor-pointer"
          onClick={() =>
            window.open(
              `${process.env.REACT_APP_API_IMAGE_URL}${value}`,
              "_blank",
            )
          }
        />
      );
    }

    // PDF
    if (isPdfFile(value)) {
      return (
        <a
          href={`${process.env.REACT_APP_API_IMAGE_URL}${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline font-medium"
        >
          📄 View PDF
        </a>
      );
    }

    // Normal text
    return value;
  };
  return (
    <>
      <RemarkModal
        id_data={serviceId}
        block_data={serviceBlock}
        title_data={title}
      ></RemarkModal>

      <DeleteModal id={serviceId} title={title} />
      <ViewModal id={serviceId} title={title} data={FranchiseModelData} />

      <MainDrawer>
        <FranchiseDrawer id={serviceId} onModalOpen={handleSuccessModalOpen} />
      </MainDrawer>

      <TableBody>
        {franchise?.slice(0, perpage).map((data, index) => (
          <TableRow key={data?.id}>
            <TableCell className="text-center text-sm">
              {(currentPage - 1) * perpage + index + 1}
            </TableCell>
            <TableCell className="text-center text-sm">
              {data.company_name || "-"}
            </TableCell>
            <TableCell className="text-center text-sm">
              {data.owner_name || "-"}
            </TableCell>
            <TableCell className="text-center text-sm">
              {data?.service_cities?.length > 0
                ? (() => {
                    const cityNames = data.service_cities
                      .map((item) => item.name)
                      .join(", ");
                    return cityNames.length > 100
                      ? `${cityNames.slice(0, 50)}...`
                      : cityNames;
                  })()
                : "-"}
            </TableCell>
            <TableCell className="text-center text-sm">
              {data?.service_states?.length > 0
                ? (() => {
                    const stateNames = data.service_states
                      .map((item) => item.name)
                      .join(", ");
                    return stateNames.length > 100
                      ? `${stateNames.slice(0, 50)}...`
                      : stateNames;
                  })()
                : "-"}
            </TableCell>
            <TableCell className="text-center text-sm">
              {data.email_id || "-"}
            </TableCell>
            <TableCell className="text-center text-sm">
              {data.phone_no || "-"}
            </TableCell>
            <TableCell className="text-center">
              <>
                <div
                  className={`text-xs font-semibold mt-1 ${
                    data.account_status === "active"
                      ? "text-green-600"
                      : data.account_status === "blocked"
                        ? "text-red-600"
                        : "text-gray-500"
                  }`}
                >
                  {data.account_status === "active"
                    ? "Active"
                    : data.account_status === "blocked"
                      ? "Blocked"
                      : "Inactive"}
                </div>
                {hideEdit && (
                  <FranchiseStatus
                    id={data.id}
                    accountStatus={data.account_status}
                    title_block="Franchise"
                  />
                )}
              </>
            </TableCell>

            {/* <TableCell className="text-center">
              <span
                className="text-sm cursor-pointer"
                onClick={() => {
                  setSelectedData(data);
                  setOpenRemarks(true);
                }}
              >
                view
              </span>
            </TableCell> */}

            <TableCell className="text-sm text-center">
              {(() => {
                if (tab !== 1)
                  return <span className="text-gray-400 text-xs">—</span>;
                const changes =
                  data.approval_status?.type === "update"
                    ? data.pending_changes
                    : data.update_rejected
                      ? data.latest_history?.changes_json
                      : null;
                return Array.isArray(changes) && changes.length > 0 ? (
                  <button
                    onClick={() => {
                      setChangesModalData(changes);
                      setChangesModalOpen(true);
                    }}
                    className="text-xs text-blue-600 underline hover:text-blue-800 cursor-pointer"
                  >
                    {changes.length} change{changes.length > 1 ? "s" : ""}
                  </button>
                ) : (
                  <span className="text-gray-400 text-xs">—</span>
                );
              })()}
            </TableCell>

            <TableCell className="text-center">
              {tab === 1 && data.approval_status?.type ? (
                <div className="cursor-pointer">
                  <span className="block text-yellow-600 font-semibold text-xs mb-1">
                    {data.approval_status.type === "update"
                      ? "Update Pending"
                      : "New Registration"}
                  </span>
                  <span
                    className="text-green-600 hover:underline"
                    onClick={() => {
                      setSelectedId(data.id);
                      setActionType("approve");
                      setConfirmOpen(true);
                    }}
                  >
                    Approve
                  </span>
                  {" / "}
                  <span
                    className="text-red-600 hover:underline"
                    onClick={() => {
                      setSelectedId(data.id);
                      setActionType("reject");
                      setConfirmOpen(true);
                    }}
                  >
                    Reject
                  </span>
                </div>
              ) : tab === 1 && data.update_rejected ? (
                <div>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    Update Rejected
                  </span>
                  {data.update_rejection_reason && (
                    <div>
                      <button
                        className="text-xs text-blue-600 hover:underline mt-1"
                        onClick={() => {
                          setViewReason(data.update_rejection_reason);
                          setReasonModalOpen(true);
                        }}
                      >
                        Reason
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      data.status === "approved"
                        ? "bg-green-100 text-green-700"
                        : data.status === "rejected"
                          ? "bg-red-100 text-red-700"
                          : data.status === "pending"
                            ? "bg-yellow-100 text-orange-600"
                            : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {data.status}
                  </span>
                  {data.status === "rejected" && data.rejection_reason && (
                    <div>
                      <button
                        className="text-xs text-blue-600 hover:underline mt-1"
                        onClick={() => {
                          setViewReason(data.rejection_reason);
                          setReasonModalOpen(true);
                        }}
                      >
                        Reason
                      </button>
                    </div>
                  )}
                </>
              )}
            </TableCell>
            <TableCell>
              <div className="flex justify-center">
                <div className="p-2 cursor-pointer text-gray-400 hover:text-green-600">
                  <button
                    onClick={async () => {
                      await FranchisegetByIdData(data?.id);
                      handleView(data.id, "Franchise Details");
                    }}
                    className="p-2 cursor-pointer text-gray-400 hover:text-red-600 focus:outline-none"
                  >
                    <FiEye />
                  </button>
                </div>
                <EditDeleteButton
                  title={"Franchise"}
                  id={data.id}
                  handleUpdate={handleUpdate}
                  handleModalOpen={handleModalOpen}
                  hideDelete={hideDelete}
                  hideEdit={hideEdit}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>

      <Modal isOpen={isOpen} onClose={handleSuccessModalClose}>
        <div className="flex justify-between items-center p-2">
          <p className="text-lg font-semibold">Success</p>
          <button
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={handleSuccessModalClose}
          >
            <strong>✕</strong>
          </button>
        </div>
        <ModalBody>
          <div>
            <p>Franchise Added Successfully</p>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={changesModalOpen}
        onClose={() => setChangesModalOpen(false)}
        style={{ height: "500px", overflow: "scroll" }}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <p className="text-base font-semibold">Requested Changes</p>
          <button
            onClick={() => setChangesModalOpen(false)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <strong>✕</strong>
          </button>
        </div>
        <ModalBody>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left px-3 py-2 border border-gray-200 font-semibold text-gray-600">
                  Field
                </th>

                <th className="text-left px-3 py-2 border border-gray-200 font-semibold text-red-500">
                  Before
                </th>

                <th className="text-left px-3 py-2 border border-gray-200 font-semibold text-green-600">
                  Requested
                </th>
              </tr>
            </thead>

            <tbody>
              {changesModalData.map((c, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-3 py-2 border border-gray-200 font-medium text-gray-700">
                    {c.label}
                  </td>

                  <td className="px-3 py-2 border border-gray-200 text-red-500">
                    {renderChangeValue(c.old, c, "old")}
                  </td>

                  <td className="px-3 py-2 border border-gray-200 text-green-600 font-semibold">
                    {renderChangeValue(c.new, c, "new")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModalBody>
      </Modal>

      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <ModalBody>
          <h2 className="text-lg font-semibold mb-4">
            {actionType === "approve"
              ? "Approve Franchise"
              : "Reject Franchise"}
          </h2>

          <p className="mb-4">
            Are you sure you want to{" "}
            <strong>{actionType === "approve" ? "approve" : "reject"}</strong>{" "}
            this franchise?
          </p>

          {actionType === "reject" && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Reason <span className="text-red-500">*</span>
              </label>

              <textarea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border rounded-md p-2 focus:outline-none focus:ring focus:border-blue-500"
                placeholder="Enter rejection reason..."
              />
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 border rounded"
              onClick={() => {
                setConfirmOpen(false);
                setReason("");
              }}
            >
              Cancel
            </button>

            <button
              className={`px-4 py-2 rounded text-white ${
                actionType === "approve" ? "bg-green-600" : "bg-red-600"
              }`}
              onClick={handleConfirm}
            >
              Confirm
            </button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={reasonModalOpen} onClose={() => setReasonModalOpen(false)}>
        <div className="flex justify-between items-center p-2">
          <p className="text-lg font-semibold">Rejection Reason</p>
          <button
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={() => setReasonModalOpen(false)}
          >
            {/* <strong>✕</strong> */}
          </button>
        </div>
        <ModalBody>
          <p>{viewReason}</p>
        </ModalBody>
      </Modal>
    </>
  );
};

export default FranchiseTable;
